<template>
  <div class="box-border w-full h-screen bg-[#f5fefeff] overflow-hidden font-['Raleway'] relative">
    <!-- Guard Screens: Already Signed -->
    <div
      v-if="alreadySigned && !completed"
      class="absolute inset-0 w-full h-full bg-[#eef1f0ff] flex items-center justify-center z-50"
    >
      <div class="box-border w-[480px] h-[320px] bg-white rounded-[20px] relative flex flex-col items-center shadow-md">
        <div class="w-[64px] h-[64px] bg-[#043f42ff] rounded-full absolute top-[36px] flex items-center justify-center text-white text-2xl">
          i
        </div>
        <h2 class="text-[18px] text-[#043f42ff] font-bold text-center mt-[124px] w-full">
          Already Signed
        </h2>
        <p class="text-[13px] text-[#043f42ff] font-bold text-center mt-[24px]">
          Cohort Document
        </p>
        <p class="text-[12px] text-[#888888ff] text-center mt-[12px]">
          Submitted on {{ new Date().toLocaleDateString() }}
        </p>
        <p class="text-[13px] text-[#888888ff] text-center mt-[16px]">
          You have already completed the bulk signing process for this cohort.
        </p>
        <button
          v-if="isTrainer || returnUrl"
          class="mt-4 px-6 py-2 bg-[#043f42ff] text-white text-[14px] font-bold rounded-full hover:bg-[#032e30] transition-colors shadow-md cursor-pointer"
          @click="forceRedirect"
        >
          Return to Programme View
        </button>
      </div>
    </div>

    <!-- Guard Screens: Completed / Overlay -->
    <div
      v-else-if="completed || showSuccessOverlay"
      class="fixed inset-0 w-full h-full bg-[#043f42]/40 backdrop-blur-md flex items-center justify-center z-50 p-4 transition-opacity duration-500 font-['Raleway']"
    >
      <div class="relative bg-white rounded-[24px] shadow-2xl p-8 sm:p-10 max-w-[500px] w-full flex flex-col items-center text-center border border-black/[0.06]">
        <!-- Top-right Dismiss Control -->
        <button
          v-if="!submitting"
          type="button"
          class="absolute top-5 right-5 text-gray-400 hover:text-[#043f42] transition-colors p-1 cursor-pointer"
          @click="handleCloseWindow"
        >
          <svg
            class="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <template v-if="submitting">
          <div class="w-[72px] h-[72px] rounded-full flex items-center justify-center text-[#01d680] mb-6 animate-spin">
            <svg
              class="w-10 h-10"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
          <h2 class="text-[22px] sm:text-[24px] text-[#043f42] font-extrabold mb-3">
            {{ loadingText }}
          </h2>
          <p class="text-[14px] text-gray-600 font-medium leading-relaxed max-w-[380px]">
            {{ loadingDescription }}
          </p>
        </template>

        <template v-else>
          <div class="w-[72px] h-[72px] bg-[#01d680] text-[#043f42] rounded-full flex items-center justify-center mb-6 shadow-md">
            <svg
              class="w-9 h-9"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            ><path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="3"
              d="M5 13l4 4L19 7"
            /></svg>
          </div>

          <h2 class="text-[22px] sm:text-[24px] text-[#043f42] font-extrabold mb-3">
            {{ isSponsor && !isTrainer ? 'Agreements Successfully Signed' : 'Signatures Captured & Replicated' }}
          </h2>

          <p class="text-[14px] text-gray-600 mb-6 font-medium leading-relaxed max-w-[400px]">
            {{ isSponsor && !isTrainer
              ? 'Your signature has been securely captured and applied to all learner agreements in this cohort in accordance with SETA tripartite requirements.'
              : 'Your signature has been securely captured and replicated across all learner enrollments for this cohort, in full compliance with SETA requirements.' }}
          </p>

          <div
            v-if="isSponsor && !isTrainer"
            class="w-full bg-[#f8faf9] rounded-[16px] border border-[#01d680]/20 p-4 mb-6 text-[13px] text-[#043f42] text-left leading-relaxed"
          >
            <p class="font-medium">
              A certified copy of the executed agreements and the Master Audit Trail will be distributed by the Training Provider once all signatories have concluded.
            </p>
          </div>

          <button
            v-if="isSponsor && !isTrainer"
            type="button"
            class="w-full max-w-[320px] h-[48px] rounded-full bg-[#043f42] hover:bg-[#032e30] text-white font-bold text-[15px] flex items-center justify-center transition-all shadow-md cursor-pointer mb-3"
            @click="handleCloseWindow"
          >
            Close Window
          </button>
          <button
            v-else
            type="button"
            class="w-full max-w-[320px] h-[48px] rounded-full bg-[#043f42] hover:bg-[#032e30] text-white font-bold text-[15px] flex items-center justify-center transition-all shadow-md cursor-pointer mb-3"
            @click="forceRedirect"
          >
            Return to Programme View
          </button>

          <p
            v-if="isSponsor && !isTrainer"
            class="text-[12px] text-gray-400 font-normal"
          >
            If this window does not close automatically, you can safely close your browser tab.
          </p>
        </template>
      </div>
    </div>

    <!-- Main Workspace (wnfD4) -->
    <div
      v-else
      class="box-border w-full pt-[20px] px-[20px] flex flex-col gap-[20px] h-full pb-[20px]"
    >
      <!-- Header -->
      <div class="box-border w-full shrink-0 flex flex-row justify-between items-end">
        <div class="flex flex-row gap-[22px] items-center">
          <div class="w-[41.2px] h-[41.2px] overflow-hidden flex items-center justify-center">
            <img
              src="/iconmark-3.svg"
              class="w-full h-full object-contain"
              alt="Doccu Logo"
            >
          </div>
          <div
            class="text-[28px]/[normal] text-[#000000ff] font-bold whitespace-nowrap"
            :title="props.programmeName"
          >
            {{ portalTitle }}
          </div>
        </div>
        <div class="flex flex-row gap-[13px] items-center">
          <button
            v-if="isTrainer || returnUrl"
            type="button"
            class="flex flex-row gap-[10px] px-5 py-2.5 h-[40px] whitespace-nowrap items-center bg-[#043f42] text-white rounded-full hover:bg-[#032e30] transition-colors text-[14px] font-bold border-none cursor-pointer"
            @click="forceRedirect"
          >
            Back to Programme
          </button>
          <a
            href="/dashboard"
            class="flex flex-row gap-[10px] px-5 py-2.5 h-[40px] whitespace-nowrap items-center border border-[#043f42ff] rounded-full hover:bg-gray-100 transition-colors text-[14px] font-semibold text-[#043f42]"
          >
            <span>Decline / Cancel</span>
          </a>
        </div>
      </div>

      <!-- Main Body: Sidebar + Document -->
      <div class="box-border w-full h-full flex flex-row gap-[26px] items-stretch min-h-0">
        <!-- Sidebar -->
        <div class="box-border w-[275px] shrink-0 h-full bg-[#043f42ff] rounded-[20px] overflow-hidden flex flex-col pt-[42px] px-[32px] pb-[32px]">
          <div class="flex flex-col gap-[28px]">
            <!-- Dropdown -->
            <div
              ref="dropdownRef"
              class="relative w-full shrink-0"
            >
              <button
                type="button"
                class="w-full flex flex-row items-center justify-between border border-white/80 rounded-full px-[18px] py-[12px] bg-transparent hover:bg-[#065357ff] transition-all cursor-pointer group focus:outline-none focus:ring-1 focus:ring-[#01d680]"
                @click="isDropdownOpen = !isDropdownOpen"
              >
                <div class="flex items-center gap-[8px] min-w-0 pr-2">
                  <span
                    v-if="selectedEnrollmentId && signedEnrollmentIds.has(selectedEnrollmentId)"
                    class="w-[14px] h-[14px] shrink-0 rounded-full bg-[#01d680ff] text-black flex items-center justify-center text-[9px] font-bold"
                    title="Signed"
                  >
                    ✓
                  </span>
                  <span class="text-white text-[11px] font-['Raleway',sans-serif] font-medium truncate">
                    {{ selectedStudentName || 'Student name goes here' }}
                  </span>
                </div>
                <div
                  class="w-[14px] h-[14px] shrink-0 flex items-center justify-center text-white transition-transform duration-200"
                  :class="{ 'rotate-180': isDropdownOpen }"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
              </button>

              <!-- Dropdown Menu -->
              <transition
                enter-active-class="transition duration-150 ease-out"
                enter-from-class="transform scale-95 opacity-0"
                enter-to-class="transform scale-100 opacity-100"
                leave-active-class="transition duration-100 ease-in"
                leave-from-class="transform scale-100 opacity-100"
                leave-to-class="transform scale-95 opacity-0"
              >
                <div
                  v-if="isDropdownOpen"
                  class="absolute top-[calc(100%+6px)] left-0 w-full z-40 bg-[#032e30ff] border border-white/20 rounded-[14px] shadow-2xl overflow-hidden py-1.5 flex flex-col backdrop-blur-md"
                >
                  <div
                    v-if="(submissionDataset?.enrollments?.length || 0) > 10"
                    class="px-2.5 pb-1.5"
                  >
                    <input
                      v-model="studentSearchQuery"
                      type="text"
                      placeholder="Search student..."
                      class="w-full px-2.5 py-1 text-[11px] text-white bg-white/10 border border-white/20 rounded-md outline-none focus:border-[#01d680ff] placeholder-white/40"
                      @click.stop
                    >
                  </div>

                  <div class="max-h-[220px] overflow-y-auto space-y-0.5 doccu-scrollbar">
                    <div
                      v-for="student in filteredEnrollments"
                      :key="student.id"
                      class="px-3 py-2 flex items-center justify-between cursor-pointer transition-colors text-left"
                      :class="student.id === selectedEnrollmentId ? 'bg-[#065357ff] text-[#01d680ff] font-bold' : 'text-white hover:bg-[#065357ff]/60'"
                      @click="selectStudent(student.id)"
                    >
                      <span class="text-[11px] font-['Raleway',sans-serif] truncate pr-2">
                        {{ student.student_name }}
                      </span>
                      <span
                        v-if="signedEnrollmentIds.has(student.id)"
                        class="shrink-0 flex items-center gap-1 text-[9px] font-semibold text-[#01d680ff]"
                      >
                        <svg
                          class="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2.5"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        Signed
                      </span>
                      <span
                        v-else
                        class="shrink-0 text-[9px] text-white/40"
                      >
                        Pending
                      </span>
                    </div>
                  </div>
                </div>
              </transition>
            </div>

            <!-- Instructions box (Notice Card) -->
            <div class="w-full flex flex-col gap-[10px] p-[11px_9px] border border-white rounded-[14px]">
              <div class="flex flex-col gap-[39px] items-center">
                <div class="flex flex-col gap-[10px] w-full">
                  <h3 class="text-[16px] text-white font-bold leading-none">
                    Know this
                  </h3>
                  <p class="text-[10px]/[15px] text-white">
                    You can individually sign for each student but should you choose to sign for the first student, the remaining student will be automatically pre-filled for you.
                  </p>
                </div>
                <div class="flex flex-row gap-[8px] p-[4px_11px] items-center bg-[#065357ff] rounded-lg w-full">
                  <div class="w-[13px] h-[13px] shrink-0 flex items-center justify-center text-white">
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M12 20h9" />
                      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                    </svg>
                  </div>
                  <p class="text-[10px]/[15px] text-white flex-1">
                    You can save time and bulk sign duplicating documents, at the same time.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Bottom Actions -->
          <div class="mt-auto flex flex-col gap-[35px] items-center w-full">
            <div class="w-full flex flex-col gap-[11px]">
              <div class="text-[10px]/[20px] text-white text-left w-full">
                {{ signedEnrollmentIds.size }} of {{ submissionDataset?.enrollments?.length || 0 }} Students Signed for
              </div>
              <div class="w-full h-[13px] bg-[#a9f8d8ff] rounded-full overflow-hidden relative">
                <div
                  class="h-full bg-[#00721bff] rounded-full transition-all duration-300"
                  :style="{ width: `${(signedEnrollmentIds.size / (submissionDataset?.enrollments?.length || 1)) * 100}%` }"
                />
              </div>
            </div>

            <button
              :disabled="submitting || signedEnrollmentIds.size === 0 || remainingCount === 0"
              class="w-full max-w-[211px] flex flex-row gap-[8px] py-[10px] px-[14px] items-center justify-center rounded-full transition-colors"
              :class="signedEnrollmentIds.size === 0 || remainingCount === 0 ? 'bg-gray-400 cursor-not-allowed text-gray-700 opacity-50' : 'bg-[#01d680ff] hover:bg-[#01b56c] cursor-pointer text-black'"
              @click="startOverlayAndComplete"
            >
              <div class="w-[13px] h-[13px] shrink-0 flex items-center justify-center currentColor">
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M12 20h9" />
                  <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                </svg>
              </div>
              <span class="text-[13px]/[18px] font-semibold whitespace-nowrap">
                {{ signButtonText }}
              </span>
            </button>
            <p
              v-if="signedEnrollmentIds.size === 0"
              class="text-[11px] text-white/70 text-center leading-relaxed max-w-[260px]"
            >
              Sign the first document in the viewer to establish your signature, then apply across all recipients.
            </p>
            <p
              v-else-if="remainingCount > 0"
              class="text-[11px] text-white/70 text-center leading-relaxed max-w-[260px]"
            >
              {{ signedEnrollmentIds.size }} of {{ submissionDataset?.enrollments?.length || 0 }} signed manually. Click to apply signature to remaining {{ remainingCount }} {{ remainingCount === 1 ? 'document' : 'documents' }}.
            </p>
          </div>
        </div>

        <!-- Document Viewer -->
        <div class="flex-1 bg-white rounded-[16px] overflow-hidden flex flex-col relative h-full shadow-xs border border-black/[0.08]">
          <div
            v-if="iframeLoading"
            id="portal-stage-loader"
            class="absolute inset-0 flex flex-col items-center justify-center bg-[#f9fafb]/90 z-20 transition-opacity duration-200 pointer-events-none"
          >
            <svg
              class="animate-spin h-9 w-9 mb-3 text-[#01d680]"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span class="text-[13px] font-semibold text-[#043f42] font-['Raleway']">Opening PDF...</span>
          </div>

          <template v-if="selectedEnrollment">
            <iframe
              v-if="currentViewerUrl"
              :key="currentViewerKey"
              :src="currentViewerUrl"
              class="w-full h-full border-none rounded-[16px]"
              title="Learnership Agreement"
              @load="onIframeLoad"
            />
            <div
              v-else
              class="w-full h-full flex items-center justify-center text-gray-500 flex-col gap-2"
            >
              <span class="text-2xl">⚠️</span>
              <span>No document found for this enrollment.</span>
              <span class="text-sm">Ensure the enrollment submission has an active agreement attached.</span>
            </div>
          </template>
          <div
            v-else
            class="w-full h-full flex items-center justify-center text-gray-400 font-['Raleway']"
          >
            Select a student to view their document
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'

const props = defineProps({
  programmeName: {
    type: String,
    default: ''
  },
  isTrainer: {
    type: Boolean,
    default: false
  },
  isSponsor: {
    type: Boolean,
    default: false
  },
  alreadySigned: {
    type: Boolean,
    default: false
  },
  completed: {
    type: Boolean,
    default: false
  },
  submitUrl: {
    type: String,
    required: true
  },
  returnUrl: {
    type: String,
    default: ''
  },
  submissionDataset: {
    type: Object,
    default: () => ({ enrollments: [] })
  }
})

const submissionDataset = ref(props.submissionDataset)
const selectedEnrollmentId = ref(null)
const iframeLoading = ref(true)

const portalTitle = computed(() => {
  const name = (props.programmeName && props.programmeName.trim() !== '')
    ? props.programmeName
    : (props.isTrainer ? 'Training Provider Bulk Signing' : 'Bulk Signing')
  return name.length > 20 ? `${name.slice(0, 20)}...` : name
})

const isDropdownOpen = ref(false)
const dropdownRef = ref(null)
const studentSearchQuery = ref('')

const selectedStudentName = computed(() => {
  return selectedEnrollment.value?.student_name || ''
})

const filteredEnrollments = computed(() => {
  const list = submissionDataset.value?.enrollments || []
  if (!studentSearchQuery.value) return list
  const q = studentSearchQuery.value.toLowerCase().trim()
  return list.filter(s => (s.student_name || '').toLowerCase().includes(q))
})

const selectStudent = (id) => {
  selectedEnrollmentId.value = id
  isDropdownOpen.value = false
  studentSearchQuery.value = ''
}

const handleClickOutside = (e) => {
  if (dropdownRef.value && !dropdownRef.value.contains(e.target)) {
    isDropdownOpen.value = false
  }
}

const handleKeydown = (e) => {
  if (e.key === 'Escape') {
    isDropdownOpen.value = false
  }
}

const remainingCount = computed(() => {
  const total = submissionDataset.value?.enrollments?.length || 0
  const signed = signedEnrollmentIds.value.size
  return Math.max(0, total - signed)
})

const signButtonText = computed(() => {
  const remaining = remainingCount.value
  const signed = signedEnrollmentIds.value.size

  if (signed === 0) {
    return 'Bulk Sign Remaining'
  }

  if (remaining === 0) {
    return 'All Documents Signed'
  }

  return 'Bulk Sign Remaining'
})

const selectedEnrollment = computed(() => {
  if (!submissionDataset.value?.enrollments || !selectedEnrollmentId.value) return null
  return submissionDataset.value.enrollments.find(e => e.id === selectedEnrollmentId.value)
})

const currentViewerUrl = computed(() => {
  if (!selectedEnrollment.value) return ''
  return selectedEnrollment.value.trainer_submitter_url || selectedEnrollment.value.submitter_url || ''
})

const currentViewerKey = computed(() => {
  return `${selectedEnrollmentId.value}-${currentViewerUrl.value}`
})

watch(selectedEnrollmentId, () => {
  iframeLoading.value = true
  setTimeout(() => {
    iframeLoading.value = false
  }, 400)
})

const submitting = ref(false)
const showSuccessOverlay = ref(false)

const signedEnrollmentIds = ref(new Set())

const advanceToNextOrComplete = () => {
  if (selectedEnrollmentId.value) {
    signedEnrollmentIds.value.add(selectedEnrollmentId.value)
  }

  const enrollments = submissionDataset.value?.enrollments || []

  if (signedEnrollmentIds.value.size >= enrollments.length) {
    startOverlayAndComplete()
    return
  }

  // Find next unsigned enrollment
  const nextEnrollment = enrollments.find(e => !signedEnrollmentIds.value.has(e.id))
  if (nextEnrollment) {
    selectedEnrollmentId.value = nextEnrollment.id
  }
}

const onIframeLoad = (event) => {
  iframeLoading.value = false
  // Keeping this as a fallback just in case
  try {
    const iframeLocation = event.target.contentWindow.location.pathname
    if (iframeLocation.endsWith('/completed')) {
      advanceToNextOrComplete()
    }
  } catch (e) {
    // cross-origin error, expected sometimes
  }
}

const handleMessage = (event) => {
  if (event.data?.type === 'docuseal:completed') {
    advanceToNextOrComplete()
  }
}

onMounted(() => {
  window.addEventListener('message', handleMessage)
  document.addEventListener('click', handleClickOutside)
  document.addEventListener('keydown', handleKeydown)

  // Try parsing stringified JSON if it came through as a string from the data attribute
  if (typeof submissionDataset.value === 'string') {
    try {
      submissionDataset.value = JSON.parse(submissionDataset.value)
    } catch (e) {
      console.error('Failed to parse submissionDataset prop', e)
    }
  }

  // Populate already completed enrollments
  if (submissionDataset.value?.enrollments?.length > 0) {
    submissionDataset.value.enrollments.forEach(e => {
      if (e.is_completed) {
        signedEnrollmentIds.value.add(e.id)
      }
    })

    const firstUncompleted = submissionDataset.value.enrollments.find(e => !e.is_completed)
    if (firstUncompleted) {
      selectedEnrollmentId.value = firstUncompleted.id
    } else {
      selectedEnrollmentId.value = submissionDataset.value.enrollments[0].id
      showSuccessOverlay.value = true
      submitting.value = false
    }
  }
})

onUnmounted(() => {
  window.removeEventListener('message', handleMessage)
  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('keydown', handleKeydown)
  if (loadingInterval) clearInterval(loadingInterval)
})

const startOverlayAndComplete = () => {
  if (showSuccessOverlay.value) return
  showSuccessOverlay.value = true
  handleComplete() // Execute bulk sign backend call immediately
}

const redirectTimeout = null

const forceRedirect = () => {
  if (redirectTimeout) clearTimeout(redirectTimeout)
  if (props.returnUrl) {
    window.location.href = props.returnUrl
    return
  }
  const url = new URL(window.location.href)
  url.pathname = url.pathname.replace(/\/sign\/?$/, '')
  url.searchParams.set('success', 'bulk_signed')
  window.location.href = url.toString()
}

const handleCloseWindow = () => {
  window.close()
  if (props.isTrainer || props.returnUrl) {
    forceRedirect()
  }
}

const loadingText = ref('Processing Signatures...')
const loadingDescription = ref('Please wait while we securely apply your signature...')
let loadingInterval = null

const handleComplete = () => {
  if (submitting.value) return
  submitting.value = true

  const total = submissionDataset.value?.enrollments?.length || 1
  const startCount = signedEnrollmentIds.value.size

  loadingText.value = 'Processing documents...'
  let current = startCount
  loadingDescription.value = `Applying signature to document ${current} of ${total}...`
  loadingInterval = setInterval(() => {
    if (current < total - 1) {
      current += 1
      loadingDescription.value = `Applying signature to document ${current} of ${total}...`
    }
  }, 250)

  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

  fetch(props.submitUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken
    },
    body: JSON.stringify({
      master_enrollment_id: selectedEnrollmentId.value
    })
  }).then(async res => {
    if (loadingInterval) clearInterval(loadingInterval)
    if (res.ok) {
      loadingDescription.value = `Applying signature to document ${total} of ${total}...`
      setTimeout(() => {
        submitting.value = false
      }, 300)
    } else {
      submitting.value = false
      showSuccessOverlay.value = false
      const data = await res.json().catch(() => ({}))
      alert(data.error || 'Failed to apply bulk signatures')
    }
  }).catch(e => {
    submitting.value = false
    if (loadingInterval) clearInterval(loadingInterval)
    showSuccessOverlay.value = false
    console.error(e)
    alert(e.message || 'An error occurred during submission')
  })
}
</script>

<style scoped>
.doccu-scrollbar::-webkit-scrollbar {
  width: 4px;
}
.doccu-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.doccu-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
}
.doccu-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.4);
}
</style>
